import React, { useEffect, useRef, useState } from "react";
import {
  Pencil, Mail, Phone, StickyNote, CalendarDays, DollarSign, FileText, Plus, Activity, CheckSquare, Square,
  Loader2, Upload, Download, UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { v2ListDocuments, v2Upload, v2FetchDownloadBlob, listContracts, CONTRACT_STATUSES } from "@/lib/api";
import { KIND_META, SERVICE_CATALOG, fmtDate, fmtDateTime, statusLabel, statusTone, errMsg } from "./clientMeta";

export const Card = ({ title, action, children, testid, className = "" }) => (
  <section className={`rounded-xl border bg-white p-5 ${className}`} style={{ borderColor: "#E2E8F0" }} data-testid={testid}>
    <header className="flex items-center justify-between gap-2 mb-4">
      <h3 className="font-display text-[15px] font-semibold" style={{ color: "#0B2545" }}>{title}</h3>
      {action}
    </header>
    {children}
  </section>
);

export const EditBtn = ({ onClick, testid, label = "Edit", icon: Icon = Pencil }) => (
  <Button variant="outline" size="sm" onClick={onClick} data-testid={testid}
          className="h-8 px-3 text-[13px] font-semibold border-slate-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800 transition-colors">
    <Icon className="h-3.5 w-3.5 mr-1.5" />{label}
  </Button>
);

const Pair = ({ label, children, testid }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3 text-sm py-1.5">
    <dt style={{ color: "#64748B" }}>{label}</dt>
    <dd className="font-medium break-words" style={{ color: "#0B2545" }} data-testid={testid}>{children || "—"}</dd>
  </div>
);

export const StatusPill = ({ kind, status, testid }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap ${statusTone(kind, status)}`} data-testid={testid}>
    {statusLabel(kind, status)}
  </span>
);

const ACT_STYLE = [
  [/email|comm_/i, Mail, "bg-blue-600"],
  [/note/i, StickyNote, "bg-[#D4AF37]"],
  [/appoint|schedul|dispatch/i, CalendarDays, "bg-teal-600"],
  [/pay|invoice|billing|stripe/i, DollarSign, "bg-emerald-600"],
  [/contract|proposal|quote/i, FileText, "bg-[#0B2545]"],
  [/creat|submit|added/i, Plus, "bg-emerald-600"],
];
const actStyle = (a) => ACT_STYLE.find(([rx]) => rx.test(`${a.action} ${a.summary}`)) || [null, Activity, "bg-slate-400"];

export const Timeline = ({ items, limit, testid = "client-timeline" }) => {
  const sorted = [...(items || [])].filter((a) => a && a.ts).sort((a, b) => String(b.ts).localeCompare(String(a.ts)));
  const shown = limit ? sorted.slice(0, limit) : sorted;
  if (!shown.length) return <p className="text-sm py-4" style={{ color: "#64748B" }} data-testid={`${testid}-empty`}>No activity yet.</p>;
  return (
    <ol className="relative space-y-4" data-testid={testid}>
      {shown.map((a, i) => {
        const [, Icon, bg] = actStyle(a);
        return (
          <li key={`${a.ts}-${i}`} className="flex gap-3">
            <span className={`grid place-items-center h-7 w-7 rounded-full shrink-0 text-white ${bg}`}><Icon className="h-3.5 w-3.5" /></span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium break-words" style={{ color: "#0B2545" }}>{a.summary || a.action}</div>
              <div className="text-xs mt-0.5 flex justify-between gap-2" style={{ color: "#64748B" }}>
                <span>{fmtDateTime(a.ts)}</span>
                <span className="capitalize" style={{ color: "#2563EB" }}>{a.actor || "System"}</span>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export const ServicesChecklist = ({ services, testid = "client-services-list" }) => {
  const list = Array.from(new Set([...SERVICE_CATALOG, ...(services || [])]));
  return (
    <ul className="grid gap-2 sm:grid-cols-2" data-testid={testid}>
      {list.map((s) => {
        const on = (services || []).includes(s);
        return (
          <li key={s} className="flex items-center gap-2.5 text-sm" style={{ color: on ? "#0B2545" : "#64748B" }}>
            {on ? <CheckSquare className="h-[18px] w-[18px] text-blue-600" /> : <Square className="h-[18px] w-[18px] text-slate-300" />}{s}
          </li>
        );
      })}
    </ul>
  );
};

const ContactBlock = ({ c, i }) => (
  <div className="flex gap-3" data-testid={`client-contact-${i}`}>
    <span className="grid place-items-center h-10 w-10 rounded-full bg-blue-50 text-blue-700 shrink-0"><UserRound className="h-5 w-5" /></span>
    <div className="min-w-0 text-sm space-y-1">
      <div className="font-semibold" style={{ color: "#0B2545" }}>{c.name || "—"}</div>
      <div style={{ color: "#64748B" }}>{c.role}</div>
      {c.phone ? <a href={`tel:${c.phone}`} className="flex items-center gap-2 hover:underline" style={{ color: "#0B2545" }}><Phone className="h-3.5 w-3.5" />{c.phone}</a> : null}
      {c.email ? <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-blue-700 hover:underline break-all"><Mail className="h-3.5 w-3.5" />{c.email}</a> : null}
    </div>
  </div>
);

export const OverviewTab = ({ kind, view, onEdit, onAddNote }) => (
  <div className="grid gap-4 xl:grid-cols-2" data-testid="client-overview-tab">
    <Card title="Client Information" testid="client-info-card" action={<EditBtn onClick={() => onEdit("info")} testid="client-info-edit" />}>
      <dl>
        <Pair label="Full Name" testid="client-info-name">{view.name}</Pair>
        <Pair label="Type">{KIND_META[kind].label}</Pair>
        <Pair label="Location">{view.address}</Pair>
        <Pair label="Date Added">{fmtDate(view.createdAt)}</Pair>
        <Pair label="Status"><StatusPill kind={kind} status={view.status} /></Pair>
        {view.extra.filter(([, v]) => v).map(([k, v]) => <Pair key={k} label={k}>{v}</Pair>)}
        {kind !== "referral_partner" ? <Pair label="Referred by" testid="client-info-referral">{view.referral}</Pair> : null}
      </dl>
    </Card>
    <Card title="Primary Contact" testid="client-contact-card" action={<EditBtn onClick={() => onEdit("contact")} testid="client-contact-edit" />}>
      <ContactBlock c={view.contacts[0] || {}} i={0} />
    </Card>
    <Card title="Services" testid="client-services-card" action={<EditBtn onClick={() => onEdit("services")} testid="client-services-edit" />}>
      <ServicesChecklist services={view.services} />
    </Card>
    <Card title="Recent Activity" testid="client-activity-card" action={<EditBtn onClick={onAddNote} testid="client-activity-add-note" label="Add Note" icon={StickyNote} />}>
      <Timeline items={view.activity} limit={5} testid="client-recent-activity" />
    </Card>
  </div>
);

export const ContactsTab = ({ view, onEdit }) => (
  <Card title="Contacts" testid="client-contacts-tab" action={<EditBtn onClick={() => onEdit("contact")} testid="client-contacts-edit" label="Edit primary" />}>
    <div className="grid gap-5 md:grid-cols-2">{view.contacts.map((c, i) => <ContactBlock key={i} c={c} i={i} />)}</div>
  </Card>
);

export const ServicesTab = ({ view, onEdit }) => (
  <Card title="Services" testid="client-services-tab" action={<EditBtn onClick={() => onEdit("services")} testid="client-services-tab-edit" />}>
    <ServicesChecklist services={view.services} testid="client-services-tab-list" />
  </Card>
);

const useLoad = (fn, deps) => {
  const [state, setState] = useState({ loading: true, data: [] });
  useEffect(() => {
    let off = false;
    setState((s) => ({ ...s, loading: true }));
    fn().then((data) => !off && setState({ loading: false, data: data || [] }))
      .catch(() => !off && setState({ loading: false, data: [] }));
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
};

const Spinner = () => <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} /></div>;

export const AppointmentsTab = ({ adapter, record, kind, reloadKey, onCreate }) => {
  const { loading, data } = useLoad(() => (adapter.appointments ? adapter.appointments(record) : Promise.resolve([])), [record.id, reloadKey]);
  return (
    <Card title="Appointments" testid="client-appointments-tab"
          action={adapter.appointments ? <EditBtn onClick={onCreate} testid="client-appointments-create" label="Create" icon={Plus} /> : null}>
      {!adapter.appointments ? <p className="text-sm" style={{ color: "#64748B" }}>Appointments aren't tracked for referral partners.</p>
        : loading ? <Spinner />
        : !data.length ? <p className="text-sm" style={{ color: "#64748B" }} data-testid="client-appointments-empty">No appointments yet.</p>
        : (
          <ul className="divide-y" style={{ borderColor: "#F1F5F9" }}>
            {data.map((a) => (
              <li key={a.id + (a.at || a.date)} className="py-3 flex items-center justify-between gap-3" data-testid={`client-appointment-${a.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold" style={{ color: "#0B2545" }}>{a.service || "Appointment"}</div>
                  <div className="text-xs mt-0.5" style={{ color: "#64748B" }}>{fmtDate(a.at || a.date)}{a.time ? ` · ${a.time}` : ""}{a.address ? ` · ${a.address}` : ""}</div>
                </div>
                <StatusPill kind="consumer" status={a.status} />
              </li>
            ))}
          </ul>
        )}
    </Card>
  );
};

export const DocumentsTab = ({ adapter, record }) => {
  const [reload, setReload] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const entity = adapter.docEntity;
  const { loading, data } = useLoad(
    () => (entity ? v2ListDocuments({ entity_type: entity, entity_id: record.id }).then((d) => d.documents || []) : Promise.resolve([])),
    [record.id, reload],
  );
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("entity_type", entity); fd.append("entity_id", record.id);
      await v2Upload(fd);
      toast.success("Document uploaded.");
      setReload((n) => n + 1);
    } catch (e) { toast.error(errMsg(e, "Upload failed.")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const download = async (d) => {
    try {
      const blob = await v2FetchDownloadBlob(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = d.filename || d.display_name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed."); }
  };
  if (!entity) return <Card title="Documents" testid="client-documents-tab"><p className="text-sm" style={{ color: "#64748B" }}>Documents aren't stored for referral partners.</p></Card>;
  return (
    <Card title="Documents" testid="client-documents-tab" action={
      <>
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="client-documents-file" />
        <EditBtn onClick={() => fileRef.current?.click()} testid="client-documents-upload" label={uploading ? "Uploading…" : "Upload"} icon={uploading ? Loader2 : Upload} />
      </>
    }>
      {loading ? <Spinner /> : !data.length ? <p className="text-sm" style={{ color: "#64748B" }} data-testid="client-documents-empty">No documents for this client yet.</p> : (
        <ul className="divide-y" style={{ borderColor: "#F1F5F9" }}>
          {data.map((d) => (
            <li key={d.id} className="py-3 flex items-center gap-3" data-testid={`client-document-${d.id}`}>
              <FileText className="h-5 w-5 shrink-0" style={{ color: "#D4AF37" }} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate" style={{ color: "#0B2545" }}>{d.display_name || d.filename}</div>
                <div className="text-xs" style={{ color: "#64748B" }}>{d.category || "Document"} · {fmtDate(d.uploaded_at)}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => download(d)} data-testid={`client-document-download-${d.id}`}><Download className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export const NotesTab = ({ view, onSaveNote }) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await onSaveNote(text.trim()); setText(""); toast.success("Note added."); }
    catch (e) { toast.error(errMsg(e, "Failed to add note.")); }
    finally { setSaving(false); }
  };
  return (
    <Card title="Notes & Activity" testid="client-notes-tab">
      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note…" data-testid="client-notes-input" />
        <Button onClick={save} disabled={saving || !text.trim()} className="sm:self-end" style={{ background: "#0B2545", color: "#FFF" }} data-testid="client-notes-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add note"}
        </Button>
      </div>
      <Timeline items={view.activity} testid="client-full-activity" />
    </Card>
  );
};

export const BillingTab = ({ adapter, record, onEdit }) => (
  <Card title="Billing" testid="client-billing-tab" action={adapter.billing ? <EditBtn onClick={() => onEdit("billing")} testid="client-billing-edit" /> : null}>
    {adapter.billing ? (
      <dl className="grid md:grid-cols-2 md:gap-x-8">{adapter.billing.summary(record).map(([k, v]) => <Pair key={k} label={k} testid={`client-billing-${k.toLowerCase().replace(/\W+/g, "-")}`}>{v}</Pair>)}</dl>
    ) : <p className="text-sm" style={{ color: "#64748B" }} data-testid="client-billing-note">{adapter.billingNote(record)}</p>}
  </Card>
);

export const ContractsTab = ({ adapter, record, reloadKey, onCreate }) => {
  const link = adapter.contractLink(record);
  const { loading, data } = useLoad(
    () => (link ? listContracts({ link_type: link.type }).then((rows) => rows.filter((c) => c.link_id === link.id)) : Promise.resolve([])),
    [record.id, reloadKey],
  );
  const meta = (s) => CONTRACT_STATUSES.find((x) => x.value === s) || {};
  return (
    <Card title="Contracts" testid="client-contracts-tab" action={link ? <EditBtn onClick={onCreate} testid="client-contracts-create" label="Create" icon={Plus} /> : null}>
      {!link ? <p className="text-sm" style={{ color: "#64748B" }}>No contract can be linked to this client.</p>
        : loading ? <Spinner />
        : !data.length ? <p className="text-sm" style={{ color: "#64748B" }} data-testid="client-contracts-empty">No contracts linked to {link.label} yet.</p>
        : (
          <ul className="divide-y" style={{ borderColor: "#F1F5F9" }}>
            {data.map((c) => (
              <li key={c.id} className="py-3 flex items-center justify-between gap-3" data-testid={`client-contract-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color: "#0B2545" }}>{c.title}</div>
                  <div className="text-xs" style={{ color: "#64748B" }}>{c.start_date || "—"} → {c.end_date || "—"}</div>
                </div>
                <span className="text-[11px] font-semibold rounded-full px-2.5 py-0.5" style={{ background: meta(c.status).bg, color: meta(c.status).color }}>{meta(c.status).label || c.status}</span>
              </li>
            ))}
          </ul>
        )}
    </Card>
  );
};

export const ReferralsTab = ({ kind, view, record, allRows, onEdit, onSelect }) => {
  if (kind !== "referral_partner") {
    return (
      <Card title="Referral" testid="client-referral-tab" action={<EditBtn onClick={() => onEdit("info")} testid="client-referral-edit" />}>
        <dl><Pair label="Referred by" testid="client-referral-source">{view.referral}</Pair></dl>
      </Card>
    );
  }
  const keys = [record.name, record.organization].filter(Boolean).map((s) => s.toLowerCase());
  const referred = allRows.filter((r) => r.referral_source && keys.some((k) => r.referral_source.toLowerCase().includes(k)));
  return (
    <Card title={`Referred clients (${referred.length})`} testid="client-referred-list">
      {!referred.length ? <p className="text-sm" style={{ color: "#64748B" }}>No clients list this partner as their referral source yet.</p> : (
        <ul className="divide-y" style={{ borderColor: "#F1F5F9" }}>
          {referred.map((r) => (
            <li key={`${r.kind}:${r.id}`}>
              <button type="button" onClick={() => onSelect(r)} className="w-full py-2.5 flex justify-between text-left text-sm hover:bg-slate-50 transition-colors">
                <span style={{ color: "#0B2545" }} className="font-medium">{r.name}</span>
                <span style={{ color: "#64748B" }}>{KIND_META[r.kind].label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};
